import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarClock, X } from 'lucide-react';
import { formatPlanRenewalLabel } from '../../utils/subscriptionRenewal';

export default function PlanRenewalReminderModal({
  isOpen,
  daysRemaining = 0,
  planName = '',
  canUpgradePlan = true,
  onClose,
  onUpgrade,
}) {
  if (typeof document === 'undefined') {
    return null;
  }

  const renewalLabel = formatPlanRenewalLabel(daysRemaining);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="fixed inset-0 z-[115] flex items-center justify-center px-4"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-950/70 backdrop-blur-md"
            aria-hidden
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 10 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="plan-renewal-title"
            className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-white/20 bg-white/95 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.65)] backdrop-blur-xl"
          >
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_20%,rgba(99,102,241,0.18),transparent_40%),radial-gradient(circle_at_80%_80%,rgba(245,158,11,0.12),transparent_45%)]" />

            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 z-10 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="relative px-7 py-6">
              <div className="flex items-start gap-4 pr-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 text-white shadow-lg shadow-orange-500/25">
                  <CalendarClock size={20} strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                    Renewal reminder
                  </p>
                  <h3 id="plan-renewal-title" className="mt-1 text-xl font-bold text-slate-900">
                    Your plan is ending soon
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {planName ? (
                      <>
                        Your <span className="font-semibold text-slate-800">{planName}</span> subscription{' '}
                        {renewalLabel.toLowerCase()}. Renew now to keep uninterrupted access to JobScribe.
                      </>
                    ) : (
                      <>
                        Your subscription {renewalLabel.toLowerCase()}. Renew now to keep uninterrupted access
                        to JobScribe.
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="mt-5 rounded-xl border border-amber-200/80 bg-gradient-to-r from-amber-50 to-orange-50/80 px-4 py-3">
                <p className="text-sm font-semibold text-amber-900">{renewalLabel}</p>
                <p className="mt-0.5 text-xs text-amber-800/90">
                  {canUpgradePlan
                    ? 'Upgrade or renew from Plan & Billing before your current period ends.'
                    : 'Ask your company administrator to renew before the current period ends. Staff accounts cannot open Plan & Billing.'}
                </p>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Remind me later
                </button>
                {canUpgradePlan ? (
                  <button
                    type="button"
                    onClick={onUpgrade}
                    className="rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-600/30 transition hover:from-indigo-700 hover:via-purple-700 hover:to-indigo-800"
                  >
                    Upgrade plan
                  </button>
                ) : null}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
